import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Fade
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Notifications,
  Email,
  Schedule,
  NotificationAdd,
  CardGiftcard
} from '@mui/icons-material';
import { apiService } from '../utils/api';

const ExchangeMonitor = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 货币代码到国家代码的映射（用于flagcdn.com）
  const currencyToCountryCode = {
    'CNY': 'cn', // 中国人民币
    'USD': 'us', // 美元
    'EUR': 'eu', // 欧元（欧盟旗帜）
    'GBP': 'gb', // 英镑
    'JPY': 'jp', // 日元
    'SGD': 'sg', // 新加坡元
    'AUD': 'au', // 澳元
    'CAD': 'ca', // 加元
    'CHF': 'ch', // 瑞士法郎
    'HKD': 'hk', // 港币
    'KRW': 'kr', // 韩元
    'THB': 'th', // 泰铢
    'MYR': 'my', // 马来西亚林吉特
    'INR': 'in', // 印度卢比
  };

  // 根据汇率对获取国旗图片组件
  const getCurrencyFlags = (currencyPair) => {
    if (!currencyPair) return null;

    const currencies = currencyPair.split('/');
    if (currencies.length !== 2) return null;

    const [baseCurrency, quoteCurrency] = currencies;
    const baseCountryCode = currencyToCountryCode[baseCurrency];
    const quoteCountryCode = currencyToCountryCode[quoteCurrency];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {baseCountryCode && (
          <img
            src={`https://flagcdn.com/w40/${baseCountryCode}.png`}
            alt={`${baseCurrency} flag`}
            loading="lazy"
            style={{
              width: '20px',
              height: 'auto',
              borderRadius: '2px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        {quoteCountryCode && (
          <img
            src={`https://flagcdn.com/w40/${quoteCountryCode}.png`}
            alt={`${quoteCurrency} flag`}
            loading="lazy"
            style={{
              width: '20px',
              height: 'auto',
              borderRadius: '2px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        {(!baseCountryCode || !quoteCountryCode) && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {currencyPair}
          </Typography>
        )}
      </Box>
    );
  };

  // 状态管理
  const [exchangeRates, setExchangeRates] = useState([]);
  const [currencyPairs, setCurrencyPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  
  // 表单状态
  const [formData, setFormData] = useState({
    email: '',
    currency_pair: 'CNY/JPY', // 设置默认值为人民币兑日元
    threshold: '',
    condition: 'above'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // 通知状态
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 订阅统计状态
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [successfulAlertsCount, setSuccessfulAlertsCount] = useState(0);
  const [countLoading, setCountLoading] = useState(false);

  // 打赏二维码显示状态
  const [showDonationQR, setShowDonationQR] = useState(false);

  // 获取汇率数据
  const fetchExchangeRates = async () => {
    try {
      const response = await apiService.getExchangeRates();
      if (response.success) {
        setExchangeRates(response.rates);
        setLastUpdateTime(response.update_time);
      } else {
        throw new Error(response.error || '获取汇率数据失败');
      }
    } catch (error) {
      console.error('获取汇率数据失败:', error);
      showSnackbar('获取汇率数据失败，请稍后重试', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 获取汇率对列表
  const fetchCurrencyPairs = async () => {
    try {
      const response = await apiService.getCurrencyPairs();
      if (response.success) {
        setCurrencyPairs(response.currency_pairs);
      }
    } catch (error) {
      console.error('获取汇率对列表失败:', error);
    }
  };

  // 获取总订阅数（包括活跃和已触发的订阅）
  const fetchSubscriptionCount = async () => {
    try {
      setCountLoading(true);
      const response = await apiService.getSubscriptionCount();
      if (response.success) {
        setSubscriptionCount(response.count);
      } else {
        console.error('获取总订阅数失败:', response.error);
        // API失败时使用默认值
        setSubscriptionCount(0);
      }
    } catch (error) {
      console.error('获取总订阅数失败:', error);
      // API失败时使用默认值
      setSubscriptionCount(0);
    } finally {
      setCountLoading(false);
    }
  };

  // 获取成功提醒总数
  const fetchSuccessfulAlertsCount = async () => {
    try {
      const response = await apiService.getSuccessfulAlertsCount();
      if (response.success) {
        setSuccessfulAlertsCount(response.count);
      } else {
        console.error('获取成功提醒总数失败:', response.error);
        // API失败时使用默认值
        setSuccessfulAlertsCount(0);
      }
    } catch (error) {
      console.error('获取成功提醒总数失败:', error);
      // API失败时使用默认值
      setSuccessfulAlertsCount(0);
    }
  };

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      await Promise.all([
        fetchExchangeRates(),
        fetchCurrencyPairs(),
        fetchSubscriptionCount(),
        fetchSuccessfulAlertsCount()
      ]);
    };
    initData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 定时刷新计数器
  useEffect(() => {
    // 每30秒刷新一次计数器数据
    const interval = setInterval(() => {
      fetchSubscriptionCount();
      fetchSuccessfulAlertsCount();
    }, 30000); // 30秒

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 确保默认汇率对在数据加载后仍然有效
  useEffect(() => {
    if (currencyPairs.length > 0 && !formData.currency_pair) {
      // 如果汇率对数据已加载但表单中没有选中值，设置默认值
      const defaultPair = currencyPairs.find(pair => pair.code === 'CNY/JPY');
      if (defaultPair) {
        setFormData(prev => ({ ...prev, currency_pair: 'CNY/JPY' }));
      } else if (currencyPairs.length > 0) {
        // 如果没有找到CNY/JPY，使用第一个可用的汇率对
        setFormData(prev => ({ ...prev, currency_pair: currencyPairs[0].code }));
      }
    }
  }, [currencyPairs, formData.currency_pair]);

  // 显示通知
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // 关闭通知
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 处理打赏按钮交互
  const handleDonationClick = () => {
    // 切换二维码显示状态
    setShowDonationQR(prev => !prev);
  };

  // 处理表单输入
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 清除对应字段的错误
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // 验证表单
  const validateForm = () => {
    const errors = {};
    
    // 验证邮箱
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      errors.email = '请输入邮箱地址';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = '请输入有效的邮箱地址';
    }
    
    // 验证汇率对
    if (!formData.currency_pair) {
      errors.currency_pair = '请选择汇率对';
    }
    
    // 验证阈值
    if (!formData.threshold) {
      errors.threshold = '请输入提醒阈值';
    } else {
      const threshold = parseFloat(formData.threshold);
      if (isNaN(threshold) || threshold <= 0) {
        errors.threshold = '请输入有效的数字';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 提交订阅
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const subscriptionData = {
        ...formData,
        threshold: parseFloat(formData.threshold)
      };
      
      const response = await apiService.createExchangeSubscription(subscriptionData);
      
      if (response.success) {
        showSnackbar('订阅创建成功！当汇率达到设置条件时，我们会发送邮件通知您。', 'success');
        // 重置表单
        setFormData({
          email: '',
          currency_pair: '',
          threshold: '',
          condition: 'above'
        });
        // 更新订阅统计数字
        setSubscriptionCount(prev => prev + 1);
      } else {
        throw new Error(response.error || '创建订阅失败');
      }
    } catch (error) {
      console.error('创建订阅失败:', error);
      showSnackbar(error.message || '创建订阅失败，请稍后重试', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // 格式化汇率值
  const formatRate = (rate) => {
    return parseFloat(rate).toFixed(4);
  };

  // 格式化时间
  const formatTime = (timeStr) => {
    if (!timeStr) return '未知';

    // 后端返回的时间格式: "2025-10-04 06:59:46" (已经是北京时间)
    // 直接显示，不进行任何时区转换或Date对象解析
    // 这样可以避免JavaScript的时区转换导致的日期错误
    return timeStr;
  };

  // 移动端汇率卡片组件
  const MobileRateCard = ({ rate }) => (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.06)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          transform: 'translateY(-1px)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              fontSize: '1.5rem',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              {getCurrencyFlags(rate.currency_pair)}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5, fontSize: '1.1rem' }}>
                {rate.currency_pair}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {rate.pair_name}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={formatTime(rate.update_time)}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: '24px',
              bgcolor: 'rgba(0,119,237,0.05)',
              borderColor: 'rgba(0,119,237,0.2)',
              color: theme.palette.primary.main
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
            当前汇率
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 700,
              fontSize: '2rem',
              letterSpacing: '-0.02em'
            }}
          >
            {formatRate(rate.rate)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      background: 'linear-gradient(180deg, #FAF5EB 0%, #ffffff 100%)',
      minHeight: '100vh',
      pt: 10
    }}>
      <Container maxWidth="lg" sx={{ py: { xs: 1, md: 2 }, px: { xs: 2, md: 3 } }}>
        {/* 页面标题 - 响应式间距 */}
        <Box sx={{ mb: { xs: 2, md: 3 }, textAlign: 'center' }}>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.palette.primary.main,
              fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem' }
            }}
          >
            汇率监控
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
              px: { xs: 1, md: 0 }
            }}
          >
            实时汇率数据监控与邮件提醒服务
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Chip
              icon={<Schedule />}
              label={`最后更新: ${formatTime(lastUpdateTime)} （仅在交易时段更新）`}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{
                bgcolor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            />
          </Box>
        </Box>

        {/* 垂直布局 - 实时汇率数据在上方 */}
        <Card
          sx={{
            mb: { xs: 3, md: 4 },
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: { xs: 2, md: 3 },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              <TrendingUp />
              实时汇率数据
            </Typography>

            {/* 响应式汇率显示 */}
            {isMobile ? (
              // 移动端：卡片布局
              <Box>
                {exchangeRates.map((rate) => (
                  <MobileRateCard key={rate.currency_pair} rate={rate} />
                ))}
              </Box>
            ) : (
              // 桌面端：表格布局
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
              >
                <Table sx={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '35%' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '35%' }} />
                  </colgroup>
                  <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                    <TableRow>
                      <TableCell sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        pl: 3,
                        py: 2
                      }}>
                        汇率对
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: theme.palette.text.primary, py: 2 }}>
                        当前汇率
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, color: theme.palette.text.primary, py: 2 }}>
                        更新时间
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exchangeRates.map((rate) => (
                      <TableRow
                        key={rate.currency_pair}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(0,119,237,0.02)'
                          },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ py: 2.5, pl: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {/* 国旗图标 */}
                            <Box sx={{
                              fontSize: '1.2rem',
                              lineHeight: 1,
                              minWidth: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              {getCurrencyFlags(rate.currency_pair)}
                            </Box>
                            {/* 汇率对信息 */}
                            <Box>
                              <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: '0.95rem' }}>
                                {rate.currency_pair}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {rate.pair_name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2.5 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              fontSize: '1.15rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            {formatRate(rate.rate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2.5 }}>
                          <Chip
                            label={formatTime(rate.update_time)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.75rem',
                              height: '28px',
                              bgcolor: 'rgba(0,119,237,0.05)',
                              borderColor: 'rgba(0,119,237,0.2)',
                              color: theme.palette.primary.main
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* 垂直布局 - 设置汇率提醒在下方 */}
        <Card
          sx={{
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: { xs: 2, md: 3 },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              <Notifications />
              设置汇率提醒
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                maxWidth: '1200px', // 增加最大宽度以容纳4个字段
                mx: 'auto'
              }}
            >
              {/* 所有4个输入字段 - 同一行 */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr', // 移动端单列
                    sm: '1fr 1fr', // 小屏幕双列
                    md: '1.8fr 1.5fr 1fr 1.2fr' // 桌面端四列，为汇率对分配更多空间
                  },
                  gap: 2,
                  mb: 4
                }}
              >
                {/* 邮箱地址 */}
                <TextField
                  fullWidth
                  label="邮箱地址"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!formErrors.email}
                  helperText={formErrors.email || '用于接收汇率提醒邮件'}
                  slotProps={{
                    input: {
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '56px'
                    }
                  }}
                />

                {/* 汇率对 */}
                <FormControl fullWidth error={!!formErrors.currency_pair}>
                  <InputLabel>汇率对</InputLabel>
                  <Select
                    value={formData.currency_pair}
                    label="汇率对"
                    onChange={(e) => handleInputChange('currency_pair', e.target.value)}
                    sx={{
                      height: '56px',
                      minWidth: '200px' // 确保选择器有足够的最小宽度
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          minWidth: '250px', // 确保下拉菜单有足够宽度显示完整文本
                          maxHeight: '300px'
                        }
                      }
                    }}
                  >
                    {currencyPairs.map((pair) => (
                      <MenuItem
                        key={pair.code}
                        value={pair.code}
                        sx={{
                          whiteSpace: 'nowrap', // 防止文本换行
                          overflow: 'visible', // 确保文本完全可见
                          minHeight: '48px', // 给选项足够的高度
                          px: 2 // 增加水平内边距
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* 国旗图标 */}
                          <Box sx={{
                            fontSize: '1rem',
                            lineHeight: 1,
                            minWidth: '32px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {getCurrencyFlags(pair.code)}
                          </Box>
                          {/* 汇率对文本 */}
                          <Box>
                            {pair.code} - {pair.name}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.currency_pair && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                      {formErrors.currency_pair}
                    </Typography>
                  )}
                </FormControl>

                {/* 提醒阈值 */}
                <TextField
                  fullWidth
                  label="提醒阈值"
                  type="number"
                  slotProps={{
                    htmlInput: { step: '0.0001', min: '0' }
                  }}
                  value={formData.threshold}
                  onChange={(e) => handleInputChange('threshold', e.target.value)}
                  error={!!formErrors.threshold}
                  helperText={formErrors.threshold || '支持小数点后4位'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '56px'
                    }
                  }}
                />

                {/* 提醒条件 */}
                <FormControl fullWidth>
                  <InputLabel>提醒条件</InputLabel>
                  <Select
                    value={formData.condition}
                    label="提醒条件"
                    onChange={(e) => handleInputChange('condition', e.target.value)}
                    sx={{ height: '56px' }}
                  >
                    <MenuItem value="above">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp fontSize="small" color="success" />
                        高于阈值时提醒
                      </Box>
                    </MenuItem>
                    <MenuItem value="below">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingDown fontSize="small" color="error" />
                        低于阈值时提醒
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* 按钮区域 */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                pt: 2
              }}>
                {/* 按钮组 */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 2, sm: 2 },
                  width: '100%'
                }}>
                  {/* 创建提醒按钮 */}
                  <Button
                    type="submit"
                    variant="contained"
                    size={isMobile ? "medium" : "large"}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <NotificationAdd />}
                    sx={{
                      minWidth: { xs: '100%', sm: '180px' },
                      maxWidth: { xs: '100%', sm: '220px' },
                      height: { xs: '48px', md: '56px' },
                      background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                      boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      fontWeight: 600,
                      borderRadius: 2,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)',
                        boxShadow: '0 6px 20px rgba(76, 175, 80, 0.6)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: 'rgba(0,0,0,0.12)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {submitting ? '创建中...' : '创建提醒'}
                  </Button>

                  {/* 打赏作者按钮 */}
                  <Button
                    variant="contained"
                    size={isMobile ? "medium" : "large"}
                    startIcon={<CardGiftcard />}
                    onClick={handleDonationClick}
                    sx={{
                      minWidth: { xs: '100%', sm: '160px' },
                      maxWidth: { xs: '100%', sm: '200px' },
                      height: { xs: '48px', md: '56px' },
                      background: 'linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)',
                      boxShadow: '0 4px 15px rgba(255, 193, 7, 0.4)',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      fontWeight: 600,
                      borderRadius: 2,
                      color: '#000',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #ffb300 0%, #e65100 100%)',
                        boxShadow: '0 6px 20px rgba(255, 193, 7, 0.6)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    打赏作者
                  </Button>
                </Box>

                {/* 统计信息显示 */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  width: '100%'
                }}>
                  {countLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: { xs: '0.8rem', md: '0.85rem' },
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textAlign: 'center',
                        lineHeight: 1.4,
                        flexWrap: 'wrap',
                        justifyContent: 'center'
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', md: '0.9rem' }
                        }}
                      >
                        {subscriptionCount}
                      </Box>
                      人已创建提醒，有
                      <Box
                        component="span"
                        sx={{
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', md: '0.9rem' }
                        }}
                      >
                        {successfulAlertsCount}
                      </Box>
                      人成功蹲到了想要的汇率
                    </Typography>
                  )}
                </Box>

                {/* 打赏二维码显示 */}
                {showDonationQR && (
                  <Fade in={showDonationQR} timeout={300}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      mt: 3
                    }}>
                      <Box
                        component="img"
                        src="/images/PayCode.png"
                        alt="打赏二维码"
                        sx={{
                          width: { xs: '360px', sm: '440px' },
                          height: 'auto',
                          borderRadius: 2,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: 'none',
                          textAlign: 'center',
                          fontSize: '0.8rem'
                        }}
                      >
                        二维码加载失败，请稍后重试
                      </Typography>

                      {/* 谢谢文字 */}
                      <Typography
                        variant="h5"
                        sx={{
                          color: theme.palette.success.main,
                          textAlign: 'center',
                          fontSize: { xs: '1.5rem', sm: '1.8rem' },
                          fontWeight: 600,
                          mt: 2,
                          letterSpacing: '0.5px'
                        }}
                      >
                        谢谢！ 🥰💕
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* 通知组件 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExchangeMonitor;
